import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, TemplateRef, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { merge, of as observableOf, Subject, Observable } from "rxjs";
import { catchError, map, startWith, switchMap, takeUntil, delay, filter } from "rxjs/operators";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { SocialPlatformI } from '../../../interfaces/core.interface';
import { CoreService } from '../../../services/core.service';
import { DialogComponent } from '../../dialog/dialog.component';
import { SocialAiPlatformFormComponent } from '../../forms/create-account/platform-form/platform-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PublioMetaConnectionComponent } from '../../parts/publio-meta-connection/publio-meta-connection.component';
import { PublioTiktokConnectionComponent } from '../../parts/publio-tiktok-connection/publio-tiktok-connection.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: 'app-social-ai-platforms-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginator,
    MatIcon,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatTabsModule,
    SocialAiPlatformFormComponent,
    DialogComponent,
    MatTooltipModule,
    PublioMetaConnectionComponent,
    PublioTiktokConnectionComponent
  ],
  templateUrl: './social-ai-platforms-table.component.html',
  styleUrls: ['./social-ai-platforms-table.component.scss']
})
export class SocialAiPlatformsTableComponent implements OnInit, AfterViewInit, OnDestroy {

  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.companyIdStream$.next();
    }
  }

  get companyID(): string | null {
    return this._companyID;
  }

  @Input() tiktokPlatformId: string | null = null;
  @Input() tiktokSiteId: string | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('socialPlatformTmpl', { static: true }) socialPlatformTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeletePlatformTmpl', { static: true }) confirmDeletePlatformTmpl!: TemplateRef<any>;

  selectedPlaform: SocialPlatformI = {} as SocialPlatformI;
  dataSource = new MatTableDataSource<SocialPlatformI>();
  displayedColumns: string[] = [
    'id',
    'platform',
    'Domain',
    'status',
    // 'actions',
  ];

  totalItems: number = 0;
  private AllowNewSocialPlatform: boolean = true;

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog, private route: ActivatedRoute, private router: Router
  ) { }

  buildQueryParams(): any {
    return {
      companyID: this._companyID,
      sortBy: this.sort?.active || '',
      sortOrder: this.sort?.direction || '',
      offset: this.paginator?.pageIndex * this.paginator?.pageSize || 0,
      limit: this.paginator?.pageSize || 5
    };
  }

  ngOnInit(): void {
    // this.checkIfCanAddMorePlatforms();
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.companyIdStream$)
      .pipe(
        startWith({}),
        switchMap(() => {
          const params = this.buildQueryParams();
          return this.coreService.getData('/getSocialPlatforms', params)
            .pipe(catchError(() => observableOf({ data: [], total: 0 })));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {

        
        this.dataSource = new MatTableDataSource<SocialPlatformI>(data.data);
        this.totalItems = data.total;
      });

    setTimeout(() => {
      if (this._companyID !== null) {
        this.companyIdStream$.next();
      }
    }, 0);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addNewPlatformToSite() {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Please select a company first to add a platform.");
      return;
    }

    if (this.AllowNewSocialPlatform) {
      this.selectedPlaform = {
        companyID: Number(this.companyID)
      } as SocialPlatformI;

      this.dialog.open(DialogComponent, {
        width: '760px',
        maxWidth: '95vw',
        data: {
          content: this.socialPlatformTmpl,
          header: 'Add Platform',
          cls: '!bg-slate-600 !text-white'
        }
      });
    } else {
      this.coreService.showSnackbar(
        `You have exceeded the maximum number of social platforms allowed by your economic plan.`
      );
    }
  }

  editPlatform(row: SocialPlatformI): void {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Company ID is not set. Cannot edit platform.");
      return;
    }
    this.selectedPlaform = row;

    this.dialog.open(DialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        content: this.socialPlatformTmpl,
        header: "Edit Platform",
        cls: "!bg-slate-600 !text-white",
      }
    });
  }

  disconnectPlatform(platform: SocialPlatformI): void {
    let title, message;
    
    // Check if this is a TikTok platform and show special messaging
    if (platform.platform && platform.platform.toLowerCase() === 'tiktok') {
      title = 'Confirm Site Disconnection';
      message = `
        <div class="py-2">
          <p class="text-gray-700 mb-4">
            Are you sure you want to disconnect this site from TikTok?
          </p>
          <p class="text-gray-500 text-sm mb-6">
            Your other sites will remain connected to this TikTok account. This will only remove the connection for this specific site.
          </p>
        </div>
      `;
    } else {
      // Show generic messaging for non-TikTok platforms
      const accountName = this.getPlatformDisplayName(platform);
      title = 'Confirm Disconnection';
      message = `
        <div class="py-2">
          <p class="text-gray-700 mb-4">
            Are you sure you want to disconnect from TikTok?
          </p>
          <p class="text-gray-500 text-sm mb-6">
            This will remove the connection but won't delete any content.
          </p>
        </div>
      `;
    }
        
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '450px',
      maxWidth: '95vw',
      data: {
        title: title,
        htmlContent: message,
        showClose: false,
        buttons: [
          {
            text: 'No',
            type: 'secondary',
            action: false,
            class: 'px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 min-w-[80px] font-medium'
          },
          {
            text: 'Yes',
            type: 'danger',
            action: true,
            class: 'px-4 py-2 bg-red-600 hover:bg-red-700 focus:ring-red-500 focus:ring-offset-2 text-white rounded-lg focus:outline-none focus:ring-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 min-w-[80px] font-medium'
          }
        ]
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        // User confirmed, proceed with disconnection
        this.proceedWithDisconnection(platform);
      }
    });
  }

  private proceedWithDisconnection(platform: SocialPlatformI): void {

    // Make API call to disconnect TikTok
    this.coreService.postData('tiktok/disconnect', {
      platform_id: platform.id
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.coreService.showSnackbar('TikTok account disconnected successfully');
          // Refresh the platform list
          this.companyIdStream$.next();
        } else {
          this.coreService.showSnackbar(response.message || 'Failed to disconnect TikTok account');
        }
      },
      error: (error) => {
        console.error('Error disconnecting TikTok account:', error);
        this.coreService.showSnackbar('Error disconnecting TikTok account');
      }
    });
  }

  showReconnectMessage(platform: SocialPlatformI): void {
    this.coreService.showSnackbar('Please use the TikTok connection section above to reconnect this account');
  }

  private getPlatformDisplayName(platform: SocialPlatformI): string {
    // Fallback to social_media_id or generic text
    return platform.social_media_id ? 
      `this TikTok account (${platform.social_media_id.toString().substring(0, 8)}...)` : 
      'this TikTok account';
  }





  onTikTokDisconnected(): void {
    // Refresh the platform list when TikTok is disconnected from the connection component
    this.companyIdStream$.next();
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();
    this.companyIdStream$.next();
  }
  //Sidebar helpers 
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      if (Array.isArray(parsed)) {
        this.posts = parsed.slice(0, this.maxSidebarCards);
      } else {
        this.posts = [];
      }
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
    }
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;

  trackByAlertId = (_: number, item: Alert) => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }
}